import { useEffect, useRef, type RefObject } from 'react';

interface UseInfiniteScrollSentinelParams {
  enabled?: boolean;
  hasNextPage?: boolean;
  isFetching?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage: () => Promise<unknown>;
  rootMargin?: string;
}

export const useInfiniteScrollSentinel = (
  params: UseInfiniteScrollSentinelParams,
): RefObject<HTMLDivElement | null> => {
  const {
    enabled = true,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    rootMargin = '200px 0px',
  } = params;

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (!hasNextPage) return;
    if (isFetching || isFetchingNextPage) return;

    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;
        if (!hasNextPage) return;
        if (isFetching || isFetchingNextPage) return;
        void fetchNextPage();
      },
      { rootMargin },
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
    };
  }, [
    enabled,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    rootMargin,
  ]);

  return sentinelRef;
};
